import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Modal,
  Form,
  Select,
  DatePicker,
  Progress,
  Avatar,
  Tooltip,
  Alert,
} from "antd";
import {
  FaTasks,
  FaSearch,
  FaEye,
  FaCheckCircle,
  FaPauseCircle,
  FaEdit,
  FaPlayCircle,
  FaClock,
  FaFilter,
  FaCalendarAlt,
  FaUsers,
  FaUserShield,
  FaUserEdit,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import Api from "../api/Api";
import dayjs from "dayjs";
import "./Tarefa.css";

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const getStatusInfo = (id_status) => {
  const map = {
    1: { label: "ABERTO", color: "blue", icon: <FaPlayCircle /> },
    2: { label: "FECHADO", color: "green", icon: <FaCheckCircle /> },
    3: { label: "SUSPENSO", color: "orange", icon: <FaPauseCircle /> },
    4: { label: "EM ANÁLISE", color: "purple", icon: <FaClock /> },
  };
  return map[id_status] || { label: "DESCONHECIDO", color: "default", icon: null };
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE EDITAR TAREFA
───────────────────────────────────────────────────────────────────────────── */
const TarefaFormModal = ({ open, onClose, onSuccess, editingTarefa, userStored }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Verificar permissões
  const isDiretor = userStored?.diretor === "S";
  const isResponsavel = editingTarefa?.responsaveis?.includes(userStored?.id);
  
  // Diretor pode editar tudo, responsável só percentagem
  const canEditAll = isDiretor;
  const canEditOnlyProgress = !isDiretor && isResponsavel;

  useEffect(() => {
    if (open) {
      if (canEditAll) {
        loadUsuarios();
      }
      if (editingTarefa) {
        form.setFieldsValue({
          titulo: editingTarefa.titulo,
          descricao: editingTarefa.descricao,
          inicio: editingTarefa.inicio ? dayjs(editingTarefa.inicio) : null,
          fim: editingTarefa.fim ? dayjs(editingTarefa.fim) : null,
          id_responsaveis: editingTarefa.responsaveis || [],
          percentagem: editingTarefa.percentagem || 0,
        });
      }
    }
  }, [open, editingTarefa, form, canEditAll]);

  const loadUsuarios = async () => {
    try {
      setLoadingUsers(true);
      const response = await Api.ListUserDirecao({ id_direcao: userStored.direcao });
      setUsuarios(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar usuários.");
      setUsuarios([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        id: editingTarefa.id,
        id_etapa: editingTarefa.id_etapa,
        id_status: editingTarefa.id_status,
      };

      // Se for diretor, pode alterar tudo
      if (canEditAll) {
        payload.titulo = values.titulo;
        payload.descricao = values.descricao;
        payload.inicio = values.inicio ? values.inicio.format("YYYY-MM-DD HH:mm:ss") : null;
        payload.fim = values.fim ? values.fim.format("YYYY-MM-DD HH:mm:ss") : null;
        payload.id_responsaveis = values.id_responsaveis || [];
        payload.percentagem = values.percentagem;
      } 
      // Se for responsável, só pode alterar percentagem
      else if (canEditOnlyProgress) {
        payload.titulo = editingTarefa.titulo;
        payload.descricao = editingTarefa.descricao;
        payload.inicio = editingTarefa.inicio;
        payload.fim = editingTarefa.fim;
        payload.id_responsaveis = editingTarefa.responsaveis || [];
        payload.percentagem = values.percentagem;
      }

      await Api.AlterTarefa(payload);

      toast.success("Tarefa atualizada com sucesso!");
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      if (error.errorFields) {
        toast.error("Preencha todos os campos obrigatórios.");
      } else {
        toast.error("Erro ao atualizar tarefa.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    if (canEditAll) {
      return (
        <Space>
          <FaUserShield style={{ color: "#1677ff", fontSize: 20 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>Editar Tarefa (Diretor)</span>
        </Space>
      );
    }
    if (canEditOnlyProgress) {
      return (
        <Space>
          <FaUserEdit style={{ color: "#52c41a", fontSize: 20 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>Atualizar Progresso</span>
        </Space>
      );
    }
    return (
      <Space>
        <FaTasks style={{ color: "#1677ff", fontSize: 20 }} />
        <span style={{ fontWeight: 600, fontSize: 18 }}>Editar Tarefa</span>
      </Space>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={700}
      title={getModalTitle()}
      footer={[
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 6 }}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          style={{ borderRadius: 6, fontWeight: 500 }}
        >
          Atualizar
        </Button>,
      ]}
      bodyStyle={{ padding: "24px 24px 0" }}
    >
      <Form form={form} layout="vertical">
        {/* Alerta de permissões */}
        {canEditOnlyProgress && (
          <Alert
            message="Permissão de Responsável"
            description="Como responsável, você pode alterar apenas a percentagem de progresso da tarefa."
            type="info"
            showIcon
            icon={<FaUserEdit />}
            style={{ marginBottom: 24, borderRadius: 8 }}
          />
        )}

        {canEditAll && (
          <Alert
            message="Permissão de Diretor"
            description="Como diretor, você tem permissão para editar todos os campos desta tarefa."
            type="success"
            showIcon
            icon={<FaUserShield />}
            style={{ marginBottom: 24, borderRadius: 8 }}
          />
        )}

        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Título</span>}
          name="titulo"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <Input 
            disabled={!canEditAll}
            placeholder="Ex: Implementar módulo de relatórios" 
            size="large" 
          />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Descrição</span>}
          name="descricao"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <TextArea 
            disabled={!canEditAll}
            rows={4} 
            placeholder="Detalhes da tarefa..." 
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Data/Hora de Início</span>}
              name="inicio"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <DatePicker
                disabled={!canEditAll}
                showTime
                style={{ width: "100%" }}
                format="DD/MM/YYYY HH:mm:ss"
                placeholder="Selecione"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Data/Hora de Fim</span>}
              name="fim"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <DatePicker
                disabled={!canEditAll}
                showTime
                style={{ width: "100%" }}
                format="DD/MM/YYYY HH:mm:ss"
                placeholder="Selecione"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={
            <span style={{ fontWeight: 500 }}>
              Percentagem (%) 
              {canEditOnlyProgress && (
                <Tag color="green" style={{ marginLeft: 8 }}>
                  Você pode editar este campo
                </Tag>
              )}
            </span>
          }
          name="percentagem"
          rules={[
            { required: true, message: "Campo obrigatório" },
            {
              type: "number",
              min: 0,
              max: 100,
              message: "Deve estar entre 0 e 100",
              transform: (value) => Number(value),
            },
          ]}
        >
          <Input 
            type="number" 
            min={0} 
            max={100} 
            size="large" 
            placeholder="0-100"
            style={canEditOnlyProgress ? { borderColor: '#52c41a', borderWidth: 2 } : {}}
          />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Responsáveis</span>}
          name="id_responsaveis"
          rules={[{ required: true, message: "Selecione pelo menos um responsável" }]}
        >
          <Select
            disabled={!canEditAll}
            mode="multiple"
            placeholder="Selecione os responsáveis"
            loading={loadingUsers}
            size="large"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {usuarios.map((user) => (
              <Option key={user.id} value={user.id}>
                {user.nome}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   TAREFA PAGE
───────────────────────────────────────────────────────────────────────────── */
const Tarefa = () => {
  const [tarefas, setTarefas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("TODOS");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingTarefa, setEditingTarefa] = useState(null);

  const navigate = useNavigate();
  const userStored = JSON.parse(localStorage.getItem("user")) || {};
  const isDiretor = userStored?.diretor === "S";

  useEffect(() => {
    document.title = "Gestão de Tarefas | Dashboard";
    loadTarefas();
  }, []);

  const loadTarefas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Api.ListTarefa({ id_direcao: userStored.direcao });
      const list = Array.isArray(response) ? response : [];

      const tarefasComResponsaveis = await Promise.all(
        list.map(async (tarefa) => {
          try {
            const responsaveis = await Api.ListResponsavelTarefa({ id: tarefa.id });
            const idsResponsaveis = Array.isArray(responsaveis)
              ? responsaveis.map((r) => r.id)
              : [];
            return {
              ...tarefa,
              responsaveisNomes: Array.isArray(responsaveis) ? responsaveis : [],
              responsaveis: idsResponsaveis,
            };
          } catch {
            return { ...tarefa, responsaveisNomes: [], responsaveis: [] };
          }
        })
      );

      setTarefas(tarefasComResponsaveis);
    } catch {
      toast.error("Erro ao carregar tarefas.");
      setTarefas([]);
    } finally {
      setLoading(false);
    }
  }, [userStored.direcao]);

  const openFormModal = (tarefa) => {
    setEditingTarefa(tarefa);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingTarefa(null);
  };

  const openDetalhes = (tarefa) => {
    navigate(`/tarefas/${tarefa.id}`, { state: { tarefa } });
  };

  const isResponsavel = (tarefa) => {
    return tarefa.responsaveis?.includes(userStored.id) || false;
  };

  const canEditTarefa = (tarefa) => {
    // Diretor pode editar qualquer tarefa
    if (isDiretor) return true;
    // Responsável pode editar (mas só a percentagem)
    if (isResponsavel(tarefa)) return true;
    return false;
  };

  const countAberto = tarefas.filter((t) => t.id_status === 1).length;
  const countFechado = tarefas.filter((t) => t.id_status === 2).length;
  const countSuspenso = tarefas.filter((t) => t.id_status === 3).length;
  const countAnalise = tarefas.filter((t) => t.id_status === 4).length;

  const filtered = tarefas.filter((t) => {
    const statusInfo = getStatusInfo(t.id_status);
    const matchFilter = filter === "TODOS" || statusInfo.label === filter;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      t.titulo?.toLowerCase().includes(q) ||
      t.descricao?.toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  const columnsTarefas = [
    {
      title: "Tarefa",
      dataIndex: "titulo",
      key: "titulo",
      width: 280,
      render: (titulo, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            shape="square"
            size="large"
            style={{ backgroundColor: "#e6f4ff", color: "#1677ff", borderRadius: 8 }}
            icon={<FaTasks />}
          />
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 4 }}>
              {titulo || "—"}
            </div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Etapa: {record.etapa || "—"}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Período",
      key: "periodo",
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            Início: {record.inicio}
          </Text>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            Fim: {record.fim}
          </Text>
        </Space>
      ),
    },
    {
      title: "Progresso",
      dataIndex: "percentagem",
      key: "percentagem",
      width: 180,
      render: (percentagem) => {
        const valor = parseFloat(percentagem) || 0;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Progress
              percent={valor}
              size="small"
              status={valor === 100 ? "success" : "active"}
              style={{ flex: 1 }}
              strokeColor={{ "0%": "#1677ff", "100%": "#52c41a" }}
            />
            <Text strong style={{ minWidth: 45, textAlign: "right" }}>
              {valor}%
            </Text>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "id_status",
      key: "status",
      width: 130,
      render: (id_status) => {
        const { label, color, icon } = getStatusInfo(id_status);
        return (
          <Tag
            color={color}
            style={{
              borderRadius: 20,
              padding: "4px 12px",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {icon} {label}
          </Tag>
        );
      },
    },
    {
      title: "Responsáveis",
      key: "responsaveis",
      width: 180,
      render: (_, record) => {
        const nomes = record.responsaveisNomes?.map((r) => r.nome) || [];
        if (nomes.length === 0) return <Text type="secondary">—</Text>;
        
        const isUserResponsavel = isResponsavel(record);
        const display = nomes.slice(0, 2).join(", ");
        const extra = nomes.length > 2 ? ` +${nomes.length - 2}` : "";
        
        return (
          <Tooltip title={nomes.join(", ")}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <FaUsers style={{ color: isUserResponsavel ? "#52c41a" : "#8c8c8c" }} />
              <Text ellipsis style={{ maxWidth: 120 }}>
                {display}{extra}
              </Text>
              {isUserResponsavel && (
                <Tag color="green" style={{ marginLeft: 4, fontSize: 10, padding: "0 4px" }}>
                  VOCÊ
                </Tag>
              )}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Ações",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          {canEditTarefa(record) && (
            <Tooltip 
              title={
                isDiretor 
                  ? "Editar tarefa (Diretor)" 
                  : "Atualizar progresso (Responsável)"
              }
            >
              <Button
                type="text"
                icon={isDiretor ? <FaUserShield /> : <FaEdit />}
                onClick={() => openFormModal(record)}
                style={{ color: isDiretor ? "#722ed1" : "#1677ff" }}
              />
            </Tooltip>
          )}
          <Tooltip title="Ver detalhes">
            <Button
              type="text"
              icon={<FaEye />}
              onClick={() => openDetalhes(record)}
              style={{ color: "#52c41a" }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="tarefa-layout" style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Cabeçalho */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(22, 119, 255, 0.15)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaTasks style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Gestão de Tarefas
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}>
                    Visualize e gerencie todas as suas tarefas
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              {isDiretor && (
                <Tag 
                  color="purple" 
                  icon={<FaUserShield />}
                  style={{ 
                    fontSize: 14, 
                    padding: "8px 16px", 
                    borderRadius: 20,
                    fontWeight: 600 
                  }}
                >
                  DIRETOR
                </Tag>
              )}
            </Col>
          </Row>
        </div>

        {/* Cards de estatísticas */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          {[
            { label: "Total de Tarefas", value: tarefas.length, color: "#1677ff", bg: "#e6f4ff", icon: <FaTasks style={{ fontSize: 24, color: "#1677ff" }} /> },
            { label: "Tarefas Abertas", value: countAberto, color: "#1677ff", bg: "#e6f4ff", icon: <FaPlayCircle style={{ fontSize: 24, color: "#1677ff" }} /> },
            { label: "Em Análise", value: countAnalise, color: "#722ed1", bg: "#f9f0ff", icon: <FaClock style={{ fontSize: 24, color: "#722ed1" }} /> },
            { label: "Suspensas", value: countSuspenso, color: "#faad14", bg: "#fffbe6", icon: <FaPauseCircle style={{ fontSize: 24, color: "#faad14" }} /> },
          ].map((stat) => (
            <Col xs={24} sm={12} lg={6} key={stat.label}>
              <Card
                className="stat-card"
                bordered={false}
                style={{ borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.05)", border: "1px solid #f0f0f0" }}
                bodyStyle={{ padding: "20px 24px" }}
              >
                <Statistic
                  title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>{stat.label}</span>}
                  value={stat.value}
                  valueStyle={{ fontSize: 32, fontWeight: 700, color: stat.color }}
                  prefix={
                    <div style={{ backgroundColor: stat.bg, borderRadius: 12, padding: 10, marginRight: 16 }}>
                      {stat.icon}
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Barra de ferramentas */}
        <Card
          bordered={false}
          style={{ borderRadius: 16, marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Input
                size="large"
                prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
                placeholder="Pesquisar por título ou descrição..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ borderRadius: 30 }}
              />
            </Col>
            <Col>
              <Select
                size="large"
                style={{ width: 160 }}
                value={filter}
                onChange={setFilter}
                suffixIcon={<FaFilter style={{ color: "#1677ff" }} />}
              >
                <Option value="TODOS">Todos os status</Option>
                <Option value="ABERTO">Aberto</Option>
                <Option value="EM ANÁLISE">Em Análise</Option>
                <Option value="SUSPENSO">Suspenso</Option>
                <Option value="FECHADO">Fechado</Option>
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Tabela de tarefas */}
        <Card
          bordered={false}
          style={{ borderRadius: 16, boxShadow: "0 4px 16px rgba(0,0,0,0.05)", overflow: "hidden" }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columnsTarefas}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} tarefas`,
              style: { padding: "16px 24px" },
            }}
            locale={{ emptyText: "Nenhuma tarefa encontrada" }}
            scroll={{ x: 1200 }}
          />
        </Card>

        {/* Modal de edição */}
        <TarefaFormModal
          open={formModalOpen}
          onClose={closeFormModal}
          onSuccess={loadTarefas}
          editingTarefa={editingTarefa}
          userStored={userStored}
        />
      </Content>
    </Layout>
  );
};

export default Tarefa;